import { LazoCompanyAccountType } from "@/enums/LazoCompanyAccountType";
import { CompanyOnboarding } from "@/interfaces/onboarding-interfaces/CompanyOnboarding";
import { UserOnboarding } from "@/interfaces/onboarding-interfaces/UserOnboarding";
import { NewCustomerDTO } from "@/models/customers/NewCustomerDTO";
import { PendingCompanyPrefillDTO } from "@/models/customers/PendingCompanyPrefillDTO";
import { LazoInvitation } from "@/types/LazoInvitation";

/**
 * Field names used by the react-hook-form instance inside CompanyInformationForm. Note they do NOT
 * match the backend CompanyOnboardingDTO one-to-one: `address` ↔ `streetAddress`, `website` ↔
 * `url`, `latestFinancialStage` ↔ `startupStage`.
 */
export interface CompanyFormValues {
  companyName: string;
  legalName: string;
  description: string;
  zipCode: string;
  country: string;
  state: string;
  city: string;
  address: string;
  website: string;
  telephone: string;
  keyInvestors: string;
  delawareCCorp: boolean;
  latestFinancialStage: string;
  yearFounded: number | "";
  numberOfEmployees: string;
  industry: string;
  ebitda?: number;
  cashOnHand?: number;
  monthlyRevenue?: number;
  totalFundingReceived?: number;
}

/** Maps the backend prefill DTO into the form field names of the Create Company wizard. */
export function mapPrefillToCompanyForm(prefill: PendingCompanyPrefillDTO): Partial<CompanyFormValues> {
  const company = prefill?.companyOnboarding ?? {};
  return {
    companyName: company.companyName ?? "",
    legalName: company.legalName ?? "",
    description: company.description ?? "",
    zipCode: company.zipCode ?? "",
    country: company.country ?? "",
    state: company.state ?? "",
    city: company.city ?? "",
    address: company.streetAddress ?? "",
    website: company.url ?? "",
    telephone: company.telephone ?? "",
    keyInvestors: company.keyInvestors ?? "",
    delawareCCorp: company.delawareCCorp ?? false,
    latestFinancialStage: company.startupStage ?? "",
    yearFounded: company.yearFounded ?? "",
    numberOfEmployees: company.numberOfEmployees ?? "",
    industry: company.industry ?? "",
    ebitda: company.ebitda,
    cashOnHand: company.cashOnHand,
    monthlyRevenue: company.monthlyRevenue,
    totalFundingReceived: company.totalFundingReceived,
  };
}

/**
 * Maps the wizard form values into the backend CompanyOnboarding shape, translating the
 * mismatched field names (`address` → `streetAddress`, `website` → `url`, `latestFinancialStage`
 * → `startupStage`).
 */
export function mapCompanyFormToOnboarding(form: Partial<CompanyFormValues> & Partial<CompanyOnboarding>): CompanyOnboarding {
  return {
    ...(form as CompanyOnboarding),
    streetAddress: form.streetAddress ?? form.address,
    url: form.url ?? form.website,
    startupStage: form.startupStage ?? form.latestFinancialStage,
  };
}

export interface BuildNewCustomerDTOParams {
  accountType: LazoCompanyAccountType;
  workspaceName: string;
  companyData: CompanyOnboarding;
  ownerUserData: UserOnboarding;
  userMembers: LazoInvitation[];
  pendingCompanyExternalId?: string | null;
}

/** Builds the create-customer payload; includes pendingCompanyExternalId only when confirming. */
export function buildNewCustomerDTO(params: BuildNewCustomerDTOParams): NewCustomerDTO {
  const dto: NewCustomerDTO = {
    accountType: params.accountType,
    workspaceName: params.workspaceName,
    companyOnboarding: params.companyData,
    userCreation: params.ownerUserData,
    invitations: params.userMembers,
  };
  if (params.pendingCompanyExternalId) {
    dto.pendingCompanyExternalId = params.pendingCompanyExternalId;
  }
  return dto;
}
